import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import DictableTextarea from '@/components/DictableTextarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  HeartPulse, Upload, Sparkles, AlertTriangle, Stethoscope, Trash2, FileText,
  ExternalLink, Printer, ArrowRight
} from 'lucide-react';
import { openPrintWindow } from '@/lib/letterhead';
import ConfirmDialog from '@/components/ConfirmDialog';
import DatePickerField from '@/components/DatePickerField';

const ALERT_STYLE = {
  alta: 'bg-red-100 text-red-700 border-red-200',
  media: 'bg-amber-100 text-amber-700 border-amber-200',
  nessuno: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-sm font-semibold text-slate-800">{value ?? '-'}</p>
  </div>
);

const Delta = ({ v }) => {
  if (v === undefined || v === null) return null;
  const up = v > 0;
  return <span className={`ml-1 text-[10px] font-semibold ${up ? 'text-red-600' : 'text-emerald-600'}`}>{up ? '+' : ''}{v}</span>;
};

export const EcgTab = ({ ospiteId, ospite }) => {
  const { user, hasRole } = useAuth();
  const [lista, setLista] = useState([]);
  const [sel, setSel] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ data_esame: new Date().toISOString().slice(0, 10), indicazione: '' });
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [referto, setReferto] = useState({ testo: '', giudizio: '' });
  const [confirmDel, setConfirmDel] = useState(null);
  const token = localStorage.getItem('rsa_token');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/ecg', { params: { ospite_id: ospiteId } });
      setLista(res.data);
      setSel(prev => (prev ? res.data.find(e => e.id === prev.id) || res.data[0] || null : res.data[0] || null));
    } catch { setLista([]); }
  }, [ospiteId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setReferto({ testo: sel?.referto_medico?.testo || '', giudizio: sel?.referto_medico?.giudizio || '' });
  }, [sel?.id, sel?.referto_medico]);

  const handleUpload = async () => {
    if (!file) { toast.error('Seleziona il file del tracciato'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('ospite_id', ospiteId);
    fd.append('data_esame', form.data_esame);
    fd.append('indicazione', form.indicazione);
    try {
      const res = await api.post('/ecg/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Tracciato caricato');
      setShowUpload(false); setFile(null); setForm({ data_esame: new Date().toISOString().slice(0, 10), indicazione: '' });
      await load();
      setSel(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore caricamento'); }
    setUploading(false);
  };

  const analizza = async (id) => {
    setAnalyzing(true);
    try {
      await api.post(`/ecg/${id}/analizza`);
      toast.success('Lettura assistita completata');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore analisi'); }
    setAnalyzing(false);
  };

  const salvaReferto = async () => {
    try {
      await api.put(`/ecg/${sel.id}/referto`, referto);
      toast.success('Referto firmato');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const elimina = async () => {
    try {
      await api.delete(`/ecg/${confirmDel}`);
      toast.success('ECG eliminato');
      setSel(null); setConfirmDel(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const stampa = () => {
    if (!sel) return;
    const l = sel.lettura_ai || {};
    const nome = `${ospite?.cognome || ''} ${ospite?.nome || ''}`;
    let body = `<div class="info-grid">
      <div class="info-item"><span class="info-label">Ospite</span> <span class="info-value">${nome}</span></div>
      <div class="info-item"><span class="info-label">Data esame</span> <span class="info-value">${sel.data_esame}</span></div>
      <div class="info-item"><span class="info-label">Indicazione</span> <span class="info-value">${sel.indicazione || '-'}</span></div>
      <div class="info-item"><span class="info-label">Caricato da</span> <span class="info-value">${sel.caricato_da}</span></div>
    </div>`;
    if (sel.lettura_ai) {
      body += `<p class="section-title">Lettura Assistita (AI ${l.modello || ''}) - non refertazione</p>
      <table><tbody>
      <tr><th>Frequenza</th><td>${l.frequenza_bpm ?? '-'} bpm</td><th>Ritmo</th><td>${l.ritmo || '-'}</td></tr>
      <tr><th>PR</th><td>${l.pr_ms ?? '-'} ms</td><th>QRS</th><td>${l.qrs_ms ?? '-'} ms</td></tr>
      <tr><th>QT</th><td>${l.qt_ms ?? '-'} ms</td><th>QTc</th><td>${l.qtc_ms ?? '-'} ms</td></tr>
      <tr><th>Asse</th><td>${l.asse || '-'}</td><th>Conduzione</th><td>${l.conduzione || '-'}</td></tr>
      <tr><th>Ischemia</th><td>${l.segni_ischemia || '-'}</td><th>Ipertrofia</th><td>${l.ipertrofia || '-'}</td></tr>
      </tbody></table>`;
      if (l.anomalie?.length) body += `<p class="section-title">Reperti</p><ul>${l.anomalie.map(a => `<li>${a}</li>`).join('')}</ul>`;
      if (l.correlazione_terapia?.length) body += `<p class="section-title">Correlazione con la terapia in corso</p><ul>${l.correlazione_terapia.map(a => `<li>${a}</li>`).join('')}</ul>`;
      if (l.confronto_precedente) body += `<p class="section-title">Confronto con il tracciato precedente${l.precedente_data_esame ? ` (${l.precedente_data_esame})` : ''}</p><p>${l.confronto_precedente}</p>`;
      body += `<p class="section-title">Sintesi</p><p>${l.sintesi || ''}</p>`;
      if (sel.alert_motivi?.length) body += `<div class="alert-box ${sel.alert_livello === 'alta' ? 'alert-red' : 'alert-amber'}"><b>Alert ${sel.alert_livello}</b><br/>${sel.alert_motivi.join('<br/>')}</div>`;
      body += `<p style="font-size:9px;color:#64748b;margin-top:6px">Lettura assistita da intelligenza artificiale, misure stimate dal tracciato acquisito. Non sostituisce la refertazione del medico. Limiti: ${l.limiti || '-'} - Confidenza: ${l.confidenza || '-'}</p>`;
    }
    if (sel.referto_medico) {
      body += `<p class="section-title">Referto Medico</p><p><b>Giudizio:</b> ${sel.referto_medico.giudizio || '-'}</p><p>${sel.referto_medico.testo}</p>
      <p style="font-size:9px;color:#64748b">Firmato da ${sel.referto_medico.firmato_da} il ${new Date(sel.referto_medico.firmato_il).toLocaleString('it-IT')}</p>`;
    }
    body += `<div class="signature-row"><div class="signature-block">Il Medico</div><div class="signature-block">Data</div></div>`;
    openPrintWindow('Elettrocardiogramma', body, { subtitle: `${nome} - ECG del ${sel.data_esame}` });
  };

  const l = sel?.lettura_ai;

  return (
    <div className="space-y-4" data-testid="ecg-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><HeartPulse className="w-4 h-4 text-rose-500" />Elettrocardiogrammi</h3>
        <div className="flex gap-2">
          {sel && <Button size="sm" variant="outline" onClick={stampa} data-testid="ecg-print-btn"><Printer className="w-3.5 h-3.5 mr-1" />Stampa</Button>}
          {hasRole('infermiere', 'coordinatore', 'medico') && (
            <Dialog open={showUpload} onOpenChange={setShowUpload}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="ecg-upload-btn"><Upload className="w-3.5 h-3.5 mr-1" />Carica tracciato</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuovo Tracciato ECG</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">File (JPG, PNG, WEBP o PDF - max 12 MB) *</Label>
                    <Input type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={e => setFile(e.target.files?.[0] || null)} data-testid="ecg-file-input" />
                  </div>
                  <div><Label className="text-xs">Data esame *</Label>
                    <DatePickerField value={form.data_esame} onChange={v => setForm(f => ({ ...f, data_esame: v }))} testId="ecg-data-input" />
                  </div>
                  <div><Label className="text-xs">Indicazione clinica</Label>
                    <Input value={form.indicazione} onChange={e => setForm(f => ({ ...f, indicazione: e.target.value }))}
                      placeholder="es. controllo periodico, cardiopalmo, sincope" data-testid="ecg-indicazione-input" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowUpload(false)}>Annulla</Button>
                    <Button onClick={handleUpload} disabled={uploading} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="ecg-save-upload-btn">
                      {uploading ? 'Caricamento...' : 'Carica'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {lista.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-slate-200 rounded-xl">
          <HeartPulse className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-xs text-slate-400">Nessun tracciato ECG registrato</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
          <div className="space-y-2" data-testid="ecg-lista">
            {lista.map(e => (
              <button key={e.id} onClick={() => setSel(e)}
                className={`w-full text-left p-2.5 rounded-lg border text-xs transition-colors ${sel?.id === e.id ? 'border-rose-400 bg-rose-50' : 'border-slate-200 hover:bg-slate-50'}`}
                data-testid={`ecg-item-${e.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-slate-800">{new Date(e.data_esame).toLocaleDateString('it-IT')}</span>
                  {e.alert_livello !== 'nessuno' && <Badge className={`text-[9px] ${ALERT_STYLE[e.alert_livello]}`}>{e.alert_livello}</Badge>}
                </div>
                <p className="text-slate-500 mt-0.5 truncate">{e.indicazione || 'Nessuna indicazione'}</p>
                <div className="flex gap-1.5 mt-1">
                  {e.lettura_ai && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">AI</span>}
                  {e.referto_medico && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">refertato</span>}
                </div>
              </button>
            ))}
          </div>

          {sel && (
            <div className="space-y-3" data-testid="ecg-dettaglio">
              <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl border border-slate-200 bg-white">
                <div>
                  <p className="text-sm font-semibold text-slate-800">ECG del {new Date(sel.data_esame).toLocaleDateString('it-IT')}</p>
                  <p className="text-xs text-slate-500">{sel.original_filename} · caricato da {sel.caricato_da}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" asChild data-testid="ecg-open-file-btn">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/ecg/${sel.id}/file?auth=${token}`} target="_blank" rel="noreferrer">
                      <ExternalLink className="w-3.5 h-3.5 mr-1" />Apri tracciato
                    </a>
                  </Button>
                  <Button size="sm" onClick={() => analizza(sel.id)} disabled={analyzing}
                    className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="ecg-analizza-btn">
                    <Sparkles className="w-3.5 h-3.5 mr-1" />{analyzing ? 'Analisi in corso...' : sel.lettura_ai ? 'Rianalizza' : 'Lettura AI'}
                  </Button>
                  {hasRole('coordinatore', 'medico') && (
                    <Button size="sm" variant="outline" onClick={() => setConfirmDel(sel.id)} className="text-red-600" data-testid="ecg-delete-btn">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {sel.content_type !== 'application/pdf' && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/ecg/${sel.id}/file?auth=${token}`}
                  alt="Tracciato ECG" className="w-full rounded-xl border border-slate-200 bg-white object-contain max-h-[360px]"
                  data-testid="ecg-immagine" />
              )}

              {sel.alert_livello !== 'nessuno' && (
                <div className={`p-3 rounded-xl border text-xs ${ALERT_STYLE[sel.alert_livello]}`} data-testid="ecg-alert">
                  <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Alert automatico: priorita {sel.alert_livello}</p>
                  <ul className="mt-1 list-disc list-inside space-y-0.5">{sel.alert_motivi?.map((m, i) => <li key={i}>{m}</li>)}</ul>
                </div>
              )}

              {!l ? (
                <div className="p-4 rounded-xl border border-dashed border-slate-200 text-center">
                  <p className="text-xs text-slate-400">Nessuna lettura AI: premi "Lettura AI" per ottenere la descrizione strutturata del tracciato</p>
                </div>
              ) : (
                <div className="space-y-3 p-3 rounded-xl border border-violet-200 bg-violet-50/40" data-testid="ecg-lettura-ai">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-violet-600" />Lettura assistita AI</p>
                    <Badge variant="outline" className="text-[10px]">confidenza {l.confidenza || 'n/d'}</Badge>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white rounded-lg p-3 border border-slate-200">
                    <Field label="Frequenza" value={<>{l.frequenza_bpm ?? '-'} bpm<Delta v={l.delta_precedente?.frequenza_bpm} /></>} />
                    <Field label="PR" value={<>{l.pr_ms ?? '-'} ms<Delta v={l.delta_precedente?.pr_ms} /></>} />
                    <Field label="QRS" value={<>{l.qrs_ms ?? '-'} ms<Delta v={l.delta_precedente?.qrs_ms} /></>} />
                    <Field label="QTc" value={<span className={l.qtc_ms >= 480 ? 'text-red-600' : ''}>{l.qtc_ms ?? '-'} ms<Delta v={l.delta_precedente?.qtc_ms} /></span>} />
                    <Field label="Ritmo" value={l.ritmo} />
                    <Field label="Asse" value={l.asse} />
                    <Field label="Conduzione" value={l.conduzione} />
                    <Field label="Qualita tracciato" value={l.qualita_tracciato} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                    <div className="bg-white rounded-lg p-3 border border-slate-200">
                      <p className="font-semibold text-slate-700 mb-1">Reperti</p>
                      {l.anomalie?.length ? <ul className="list-disc list-inside space-y-0.5 text-slate-600">{l.anomalie.map((a, i) => <li key={i}>{a}</li>)}</ul> : <p className="text-slate-400">Nessun reperto rilevante</p>}
                      <p className="font-semibold text-slate-700 mt-2 mb-1">ST/T e ipertrofia</p>
                      <p className="text-slate-600">{l.segni_ischemia || '-'}{l.ipertrofia ? ` · ${l.ipertrofia}` : ''}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-slate-200">
                      <p className="font-semibold text-slate-700 mb-1">Correlazione con la terapia in corso</p>
                      {l.correlazione_terapia?.length ? <ul className="list-disc list-inside space-y-0.5 text-slate-600">{l.correlazione_terapia.map((a, i) => <li key={i}>{a}</li>)}</ul> : <p className="text-slate-400">Nessuna correlazione segnalata</p>}
                      {l.farmaci_considerati?.length > 0 && <p className="text-[10px] text-slate-400 mt-1">Farmaci considerati: {l.farmaci_considerati.join(', ')}</p>}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-3 border border-slate-200 text-xs">
                    <p className="font-semibold text-slate-700 mb-1 flex items-center gap-1"><ArrowRight className="w-3.5 h-3.5 text-slate-400" />Confronto con il tracciato precedente{l.precedente_data_esame ? ` (${l.precedente_data_esame})` : ''}</p>
                    <p className="text-slate-600">{l.confronto_precedente || 'Primo tracciato disponibile'}</p>
                  </div>

                  <div className="bg-white rounded-lg p-3 border border-slate-200 text-xs">
                    <p className="font-semibold text-slate-700 mb-1">Sintesi</p>
                    <p className="text-slate-600">{l.sintesi}</p>
                    {l.raccomandazioni?.length > 0 && (
                      <>
                        <p className="font-semibold text-slate-700 mt-2 mb-1">Raccomandazioni</p>
                        <ul className="list-disc list-inside space-y-0.5 text-slate-600">{l.raccomandazioni.map((a, i) => <li key={i}>{a}</li>)}</ul>
                      </>
                    )}
                  </div>

                  <p className="text-[10px] text-slate-500 italic">
                    Lettura assistita da AI su immagine acquisita: le misure sono STIME e non sostituiscono la refertazione del medico. {l.limiti}
                  </p>
                </div>
              )}

              <div className="p-3 rounded-xl border border-slate-200 bg-white space-y-2" data-testid="ecg-referto-section">
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Stethoscope className="w-4 h-4 text-emerald-600" />Referto del medico</p>
                {sel.referto_medico && (
                  <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-100 text-xs">
                    <p className="text-slate-700 whitespace-pre-wrap">{sel.referto_medico.testo}</p>
                    <p className="text-[10px] text-slate-500 mt-1">Firmato da {sel.referto_medico.firmato_da} il {new Date(sel.referto_medico.firmato_il).toLocaleString('it-IT')}</p>
                  </div>
                )}
                {hasRole('coordinatore', 'medico') ? (
                  <>
                    <Input value={referto.giudizio} onChange={e => setReferto(r => ({ ...r, giudizio: e.target.value }))}
                      placeholder="Giudizio sintetico (es. ECG nei limiti, FA a bassa risposta ventricolare)" className="text-xs" data-testid="ecg-giudizio-input" />
                    <DictableTextarea value={referto.testo} onChange={e => setReferto(r => ({ ...r, testo: e.target.value }))} rows={4}
                      placeholder="Referto del medico: usa il microfono per dettarlo a voce. Il testo firmato prevale sempre sulla lettura AI"
                      className="text-xs" contesto="Referto elettrocardiogramma" data-testid="ecg-referto-input" />
                    <div className="flex justify-end">
                      <Button size="sm" onClick={salvaReferto} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ecg-firma-referto-btn">
                        <FileText className="w-3.5 h-3.5 mr-1" />{sel.referto_medico ? 'Aggiorna referto' : 'Firma referto'}
                      </Button>
                    </div>
                  </>
                ) : !sel.referto_medico && <p className="text-xs text-slate-400">In attesa di refertazione medica</p>}
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={elimina}
        title="Eliminare il tracciato ECG?"
        message="Il tracciato non sara piu visibile nella cartella clinica."
      />
    </div>
  );
};

export default EcgTab;
